import { InboxContent } from '../value-objects/InboxContent';

/**
 * BC-035: Getting Things Done - InboxItem Aggregate
 * 
 * Core GTD Principle: "Your mind is for having ideas, not holding them"
 * Implements David Allen's GTD capture and clarification workflow
 */
export class InboxItem {
    readonly id: string;
    private _originalContent: InboxContent;
    readonly capturedAt: Date;
    readonly capturedByPersonId: string;
    
    private _clarification?: string;
    private _isActionable?: boolean;
    private _processingStatus: string;
    private _lastRefinedAt?: Date;
    private _refinedByPersonId?: string;

    private constructor(
        id: string,
        originalContent: InboxContent,
        capturedAt: Date,
        capturedByPersonId: string
    ) {
        this.id = id;
        this._originalContent = originalContent;
        this.capturedAt = capturedAt;
        this.capturedByPersonId = capturedByPersonId;
        this._processingStatus = 'unprocessed';
    }

    // Getters
    get originalContent(): string {
        return this._originalContent.value;
    }

    get clarification(): string | undefined {
        return this._clarification;
    }

    get isActionable(): boolean | undefined {
        return this._isActionable;
    }

    get processingStatus(): string {
        return this._processingStatus;
    }

    get lastRefinedAt(): Date | undefined {
        return this._lastRefinedAt;
    }

    get refinedByPersonId(): string | undefined {
        return this._refinedByPersonId;
    }

    /**
     * Factory method: Capture a new inbox item
     * GTD Principle: "Ubiquitous Capture"
     */
    static capture(content: string, capturedByPersonId: string): InboxItem {
        const id = `inbox-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const capturedAt = new Date();
        const inboxContent = InboxContent.create(content);
        
        return new InboxItem(id, inboxContent, capturedAt, capturedByPersonId);
    }

    /**
     * Factory method: Reconstruct from repository data
     */
    static fromRepository(data: {
        id: string;
        originalContent: string;
        capturedAt: Date;
        capturedByPersonId: string;
        clarification?: string;
        isActionable?: boolean;
        processingStatus?: string;
        lastRefinedAt?: Date;
        refinedByPersonId?: string;
    }): InboxItem {
        const inboxContent = InboxContent.create(data.originalContent);
        const item = new InboxItem(
            data.id,
            inboxContent,
            data.capturedAt,
            data.capturedByPersonId
        );
        
        item._clarification = data.clarification;
        item._isActionable = data.isActionable;
        item._processingStatus = data.processingStatus || 'unprocessed';
        item._lastRefinedAt = data.lastRefinedAt;
        item._refinedByPersonId = data.refinedByPersonId;
        
        return item;
    }

    /**
     * Refine Capture: Clean up or correct the original captured content
     * GTD Principle: "Your system must be current and complete"
     * 
     * This is NOT clarification - it's fixing typos, making the capture clearer,
     * or adding missing context to what was hastily captured.
     * 
     * @param refinedContent The cleaned up version of the original capture
     * @param refinedBy Person ID who refined the content
     */
    refineCapture(refinedContent: string, refinedBy: string): void {
        const newContent = InboxContent.create(refinedContent);
        
        this._originalContent = newContent;
        this._lastRefinedAt = new Date();
        this._refinedByPersonId = refinedBy;
        // Note: Does NOT change processing status - refining is not processing
    }

    /**
     * GTD Clarification: "What is it? Is it actionable?"
     * 
     * This is the core GTD processing step where you decide what something means
     * and whether it requires action.
     */
    clarify(clarification: string, isActionable: boolean, _clarifiedBy: string): void {
        this._clarification = clarification;
        this._isActionable = isActionable;
        this._processingStatus = 'processed';
    }

    /**
     * Convert to plain object for repository persistence
     */
    toData(): any {
        return {
            id: this.id,
            originalContent: this._originalContent.value,
            capturedAt: this.capturedAt,
            capturedByPersonId: this.capturedByPersonId,
            clarification: this._clarification,
            isActionable: this._isActionable,
            processingStatus: this._processingStatus,
            lastRefinedAt: this._lastRefinedAt,
            refinedByPersonId: this._refinedByPersonId
        };
    }
}